/**
 * Predict Account JWT：在瀏覽器內用 Privy 私鑰簽名取得 JWT（與 server 的 signPredictAccountMessage 一致）
 * 依賴 ethers，僅用於 Cloudflare 部署時查挂单；私鑰不離開瀏覽器。
 */
import { hashMessage, keccak256, hexlify, concat, getBytes, AbiCoder, TypedDataEncoder, Wallet } from 'ethers';

const CHAIN_ID = 56; // BnbMainnet
const KERNEL_DOMAIN = { name: 'Kernel', version: '0.3.1', chainId: CHAIN_ID };
const ECDSA_VALIDATOR = '0x845ADb2C711129d4f3966735eD98a9F09fC4cE57';

function hashKernelMessage(messageHash) {
  const codec = new AbiCoder();
  const value = new TextEncoder().encode('Kernel(bytes32 hash)');
  const typeHash = keccak256(hexlify(value));
  return keccak256(codec.encode(['bytes32', 'bytes32'], [typeHash, messageHash]));
}

function eip712WrapHash(messageHash, domain) {
  const domainSeparator = TypedDataEncoder.hashDomain(domain);
  const finalMessageHash = hashKernelMessage(messageHash);
  return keccak256(concat(['0x1901', domainSeparator, finalMessageHash]));
}

/**
 * 簽名 Predict Account 登入用 message，回傳 signature（與 @predictdotfun/sdk signPredictAccountMessage 一致）
 * @param {string} privateKey - Privy 私鑰（0x...）
 * @param {string} predictAccountAddress - 存款地址
 * @param {string|{ raw: string }} message - Predict GET /auth/message 回傳的 message（字串或 { raw: "0x..." }）
 * @returns {Promise<string>} signature 十六進位字串
 */
export async function signPredictAccountMessage(privateKey, predictAccountAddress, message) {
  const wallet = new Wallet(privateKey);
  let messageHash;
  if (typeof message === 'object' && message?.raw) {
    messageHash = message.raw;
  } else if (typeof message === 'string' && /^0x[a-fA-F0-9]{64}$/.test(message)) {
    messageHash = message;
  } else {
    messageHash = hashMessage(message);
  }
  const domain = { ...KERNEL_DOMAIN, verifyingContract: predictAccountAddress };
  const digest = eip712WrapHash(messageHash, domain);
  const digestBytes = getBytes(digest);
  const signedMessage = await wallet.signMessage(digestBytes);
  return concat([concat(['0x01', ECDSA_VALIDATOR]), signedMessage]);
}
